import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Shield, Building2, FileText, MessageSquare, Users, LogOut, 
  Search, ToggleLeft, ToggleRight, Trash2, Eye, RefreshCw,
  TrendingUp, AlertCircle, CheckCircle2, ChevronRight, Key, UserCheck, Edit,
  Download, Upload, Database, HardDrive
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const { user, token, logout, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [empresaToDelete, setEmpresaToDelete] = useState(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [empresaToResetPassword, setEmpresaToResetPassword] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showEditPlanModal, setShowEditPlanModal] = useState(false);
  const [empresaToEditPlan, setEmpresaToEditPlan] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [backupInfo, setBackupInfo] = useState(null);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreMode, setRestoreMode] = useState("merge");

  useEffect(() => {
    // Wait for auth to finish loading before checking user
    if (authLoading) return;
    
    if (!user || user.role !== "superadmin") {
      navigate("/super/login");
      return;
    }
    fetchData();
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, empresasRes] = await Promise.all([
        axios.get(`${API_URL}/super/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/super/empresas`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setEmpresas(empresasRes.data.empresas);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Sessão expirada ou acesso não autorizado");
        logout();
        navigate("/super/login");
      } else {
        toast.error("Erro ao carregar dados");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      const res = await axios.get(`${API_URL}/super/empresas`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search }
      });
      setEmpresas(res.data.empresas);
    } catch (error) {
      toast.error("Erro ao buscar empresas");
    }
  };

  const handleToggleActive = async (empresa) => {
    try {
      const res = await axios.put(
        `${API_URL}/super/empresas/${empresa.id}/toggle-active`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error("Erro ao alterar status da empresa");
    }
  };

  const handleDeleteEmpresa = async () => {
    if (!empresaToDelete) return;
    
    try {
      await axios.delete(
        `${API_URL}/super/empresas/${empresaToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Empresa excluída com sucesso");
      setShowDeleteModal(false);
      setEmpresaToDelete(null);
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir empresa");
    }
  };

  const handleResetPassword = async () => {
    if (!empresaToResetPassword || !newPassword) return;
    
    try {
      await axios.put(
        `${API_URL}/super/empresas/${empresaToResetPassword.id}/reset-admin-password`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { new_password: newPassword }
        }
      );
      toast.success("Senha resetada com sucesso");
      setShowResetPasswordModal(false);
      setEmpresaToResetPassword(null);
      setNewPassword("");
    } catch (error) {
      toast.error("Erro ao resetar senha");
    }
  };

  const handleEditPlan = async () => {
    if (!empresaToEditPlan || !selectedPlan) return;
    
    try {
      await axios.put(
        `${API_URL}/super/empresas/${empresaToEditPlan.id}/plano`,
        { plano: selectedPlan },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Plano alterado para ${selectedPlan.toUpperCase()}`);
      setShowEditPlanModal(false);
      setEmpresaToEditPlan(null);
      setSelectedPlan("");
      fetchData(); // Refresh data
    } catch (error) {
      toast.error("Erro ao alterar plano");
    }
  };

  const openEditPlanModal = (empresa) => {
    setEmpresaToEditPlan(empresa);
    setSelectedPlan(empresa.plano || "free");
    setShowEditPlanModal(true);
  };

  const handleImpersonate = async (empresa) => {
    try {
      const res = await axios.post(
        `${API_URL}/super/empresas/${empresa.id}/impersonate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Store impersonation data in localStorage BEFORE opening new tab
      localStorage.setItem("impersonateToken", res.data.token);
      localStorage.setItem("impersonateUser", JSON.stringify(res.data.user));
      localStorage.setItem("impersonateEmpresa", JSON.stringify(res.data.empresa));
      localStorage.setItem("impersonateActive", "true");
      
      toast.success(`Acessando como admin de ${empresa.nome}`);
      
      // Small delay to ensure localStorage is written before new tab opens
      setTimeout(() => {
        window.open(`/${empresa.slug}/admin`, "_blank");
      }, 100);
    } catch (error) {
      console.error("Error impersonating:", error);
      toast.error("Erro ao acessar como admin");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // ========== BACKUP FUNCTIONS ==========
  const fetchBackupInfo = async () => {
    try {
      const res = await axios.get(`${API_URL}/super/backup/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackupInfo(res.data);
    } catch (error) {
      toast.error("Erro ao obter informações do backup");
    }
  };

  const openBackupModal = () => {
    fetchBackupInfo();
    setShowBackupModal(true);
  };

  const handleDownloadBackup = async () => {
    try {
      setIsBackingUp(true);
      toast.info("Gerando backup... Isso pode levar alguns segundos.");
      
      const response = await axios.get(`${API_URL}/super/backup`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      
      // Get filename from response headers or generate one
      const contentDisposition = response.headers["content-disposition"];
      let filename = `ombudnow_backup_${new Date().toISOString().slice(0,19).replace(/[:-]/g, "")}.json`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Backup baixado com sucesso!");
    } catch (error) {
      console.error("Backup error:", error);
      toast.error("Erro ao gerar backup");
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleRestoreBackup = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith(".json")) {
      toast.error("Por favor, selecione um arquivo JSON de backup");
      return;
    }
    
    // Confirm before restore
    const confirmed = window.confirm(
      `Você está prestes a restaurar um backup no modo "${restoreMode}".\n\n` +
      (restoreMode === "merge" 
        ? "MODO MERGE: Dados existentes serão atualizados e novos serão adicionados. Nenhum dado será excluído."
        : "MODO REPLACE: TODOS os dados atuais serão SUBSTITUÍDOS pelo backup. Esta ação é IRREVERSÍVEL!") +
      "\n\nDeseja continuar?"
    );
    
    if (!confirmed) {
      event.target.value = "";
      return;
    }
    
    try {
      setIsRestoring(true);
      toast.info("Restaurando backup... Por favor, aguarde.");
      
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mode", restoreMode);
      
      const response = await axios.post(`${API_URL}/super/backup/restore`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      const results = response.data.results;
      
      // Show appropriate message
      if (response.data.success) {
        toast.success(response.data.message);
        
        // Show details about restored collections
        const restoredCount = Object.keys(results.restored_collections || {}).length;
        const skippedCount = (results.skipped_collections || []).length;
        
        if (skippedCount > 0) {
          toast.info(`${skippedCount} collections ignoradas por segurança: ${results.skipped_collections.join(", ")}`);
        }
        
        fetchData(); // Refresh dashboard data
        fetchBackupInfo(); // Refresh backup info
      } else {
        toast.error(response.data.message || "Erro durante a restauração");
        if (results.errors && results.errors.length > 0) {
          console.error("Restore errors:", results.errors);
          toast.error(`Erros: ${results.errors.join(", ")}`);
        }
      }
      
      console.log("Restore results:", results);
    } catch (error) {
      console.error("Restore error:", error);
      toast.error(error.response?.data?.detail || "Erro ao restaurar backup");
    } finally {
      setIsRestoring(false);
      event.target.value = ""; // Reset file input
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-700"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-700 to-indigo-800 text-white shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center backdrop-blur-sm">
                <Shield className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Super Admin</h1>
                <p className="text-sm text-purple-200">Ombudnow - Painel Global</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-sm text-purple-200">
                Olá, {user?.nome}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={openBackupModal}
                className="text-white hover:bg-white/10"
                data-testid="backup-btn"
              >
                <Database className="w-4 h-4 mr-2" />
                Backup
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-white hover:bg-white/10"
                data-testid="super-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Empresas</p>
                <p className="text-2xl font-bold text-slate-800">{stats?.total_empresas || 0}</p>
                <p className="text-xs text-green-600">{stats?.empresas_ativas || 0} ativas</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-red-500">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Denúncias</p>
                <p className="text-2xl font-bold text-slate-800">{stats?.total_denuncias || 0}</p>
                <p className="text-xs text-amber-600">{stats?.denuncias_abertas || 0} em aberto</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-amber-500">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Ouvidoria</p>
                <p className="text-2xl font-bold text-slate-800">{stats?.total_ouvidoria || 0}</p>
                <p className="text-xs text-amber-600">{stats?.ouvidoria_abertas || 0} em aberto</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Novas Empresas</p>
                <p className="text-2xl font-bold text-slate-800">{stats?.novas_empresas_mes || 0}</p>
                <p className="text-xs text-slate-400">Últimos 30 dias</p>
              </div>
            </div>
          </div>
        </div>

        {/* Chart */}
        {stats?.casos_por_mes && stats.casos_por_mes.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Evolução de Casos (6 meses)</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.casos_por_mes}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="denuncias" name="Denúncias" fill="#ef4444" />
                <Bar dataKey="ouvidoria" name="Ouvidoria" fill="#f59e0b" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Empresas List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h2 className="text-lg font-semibold text-slate-800">Empresas Cadastradas</h2>
              
              <div className="flex gap-2">
                <div className="relative flex-1 md:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar empresa..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    className="pl-9"
                    data-testid="super-search-input"
                  />
                </div>
                <Button onClick={handleSearch} variant="outline" data-testid="super-search-btn">
                  Buscar
                </Button>
                <Button onClick={fetchData} variant="outline" size="icon">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Empresa</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Slug</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Admin</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Plano</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Criado em</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Denúncias</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Ouvidoria</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {empresas.map((empresa) => (
                  <tr key={empresa.id} className="hover:bg-slate-50" data-testid={`empresa-row-${empresa.slug}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        {empresa.logo_url ? (
                          <img
                            src={empresa.logo_url.startsWith('data:') ? empresa.logo_url : (empresa.logo_url.startsWith('http') ? empresa.logo_url : `${process.env.REACT_APP_BACKEND_URL}${empresa.logo_url}`)}
                            alt={empresa.nome}
                            className="w-8 h-8 rounded-lg object-contain bg-slate-100"
                          />
                        ) : (
                          <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-slate-400" />
                          </div>
                        )}
                        <span className="font-medium text-slate-800">{empresa.nome}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="text-sm bg-slate-100 px-2 py-1 rounded">{empresa.slug}</code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {empresa.admin_email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-1">
                        {empresa.plano ? (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            empresa.plano.toLowerCase() === 'free' ? 'bg-slate-100 text-slate-700' :
                            empresa.plano.toLowerCase() === 'starter' ? 'bg-blue-100 text-blue-800' :
                            empresa.plano.toLowerCase() === 'professional' ? 'bg-purple-100 text-purple-800' :
                            empresa.plano.toLowerCase() === 'enterprise' ? 'bg-amber-100 text-amber-800' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {empresa.plano.toLowerCase() === 'free' ? 'Free' :
                             empresa.plano.toLowerCase() === 'starter' ? 'Starter' :
                             empresa.plano.toLowerCase() === 'professional' ? 'Pro' :
                             empresa.plano.toLowerCase() === 'enterprise' ? 'Enterprise' :
                             empresa.plano.charAt(0).toUpperCase() + empresa.plano.slice(1)}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                            Trial
                          </span>
                        )}
                        <button
                          onClick={() => openEditPlanModal(empresa)}
                          className="p-1 hover:bg-slate-100 rounded transition-colors"
                          title="Editar plano"
                          data-testid={`edit-plan-${empresa.slug}`}
                        >
                          <Edit className="w-3 h-3 text-slate-400 hover:text-slate-600" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-slate-600">
                      {empresa.created_at ? new Date(empresa.created_at).toLocaleDateString('pt-BR') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {empresa.total_denuncias}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        {empresa.total_ouvidoria}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {empresa.active ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle2 className="w-3 h-3" /> Ativo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                          <AlertCircle className="w-3 h-3" /> Inativo
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`/${empresa.slug}`, "_blank")}
                          title="Ver portal"
                          data-testid={`view-empresa-${empresa.slug}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleImpersonate(empresa)}
                          title="Acessar como Admin"
                          className="text-purple-600 hover:text-purple-800"
                          data-testid={`impersonate-${empresa.slug}`}
                        >
                          <UserCheck className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEmpresaToResetPassword(empresa);
                            setShowResetPasswordModal(true);
                          }}
                          title="Resetar senha"
                          data-testid={`reset-pass-${empresa.slug}`}
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(empresa)}
                          title={empresa.active ? "Desativar" : "Ativar"}
                          data-testid={`toggle-empresa-${empresa.slug}`}
                        >
                          {empresa.active ? (
                            <ToggleRight className="w-4 h-4 text-green-600" />
                          ) : (
                            <ToggleLeft className="w-4 h-4 text-slate-400" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEmpresaToDelete(empresa);
                            setShowDeleteModal(true);
                          }}
                          title="Excluir"
                          className="text-red-500 hover:text-red-700"
                          data-testid={`delete-empresa-${empresa.slug}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {empresas.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p>Nenhuma empresa encontrada</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Excluir Empresa?</h3>
              <p className="text-slate-600 mb-2">
                Você está prestes a excluir a empresa <strong>{empresaToDelete?.nome}</strong>.
              </p>
              <p className="text-sm text-red-600 mb-6">
                Esta ação é irreversível! Todos os dados (denúncias, ouvidoria, usuários) serão permanentemente excluídos.
              </p>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setEmpresaToDelete(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={handleDeleteEmpresa}
                  data-testid="confirm-delete-btn"
                >
                  Excluir
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Key className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Resetar Senha do Admin</h3>
              <p className="text-slate-600 mb-4">
                Resetar senha do admin da empresa <strong>{empresaToResetPassword?.nome}</strong>
              </p>
              
              <div className="text-left mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">Nova Senha</label>
                <Input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Digite a nova senha"
                  data-testid="new-password-input"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    setEmpresaToResetPassword(null);
                    setNewPassword("");
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  onClick={handleResetPassword}
                  disabled={!newPassword}
                  data-testid="confirm-reset-btn"
                >
                  Resetar Senha
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Plan Modal */}
      {showEditPlanModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Edit className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800">Alterar Plano</h3>
            </div>
            
            <p className="text-slate-600 mb-4">
              Alterar plano da empresa <strong>{empresaToEditPlan?.nome}</strong>
            </p>
            
            <div className="space-y-3 mb-6">
              {[
                { value: 'free', label: 'Free', desc: 'Gratuito - Recursos básicos', color: 'slate' },
                { value: 'starter', label: 'Starter', desc: 'Até 5 casos/mês', color: 'blue' },
                { value: 'professional', label: 'Professional', desc: 'Até 20 casos/mês', color: 'purple' },
                { value: 'enterprise', label: 'Enterprise', desc: 'Ilimitado + Suporte prioritário', color: 'amber' }
              ].map((plan) => (
                <label
                  key={plan.value}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedPlan === plan.value
                      ? `border-${plan.color}-500 bg-${plan.color}-50`
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="plan"
                    value={plan.value}
                    checked={selectedPlan === plan.value}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    selectedPlan === plan.value
                      ? `border-${plan.color}-500 bg-${plan.color}-500`
                      : 'border-slate-300'
                  }`}>
                    {selectedPlan === plan.value && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-slate-800">{plan.label}</div>
                    <div className="text-sm text-slate-500">{plan.desc}</div>
                  </div>
                </label>
              ))}
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowEditPlanModal(false);
                  setEmpresaToEditPlan(null);
                  setSelectedPlan("");
                }}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={handleEditPlan}
                disabled={!selectedPlan}
                data-testid="confirm-edit-plan-btn"
              >
                Salvar Plano
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Backup Modal */}
      {showBackupModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Database className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800">Backup & Restore</h3>
              </div>
              <button
                onClick={() => setShowBackupModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            
            {/* Database Info */}
            {backupInfo && (
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <HardDrive className="w-4 h-4 text-slate-600" />
                  <span className="font-medium text-slate-700">Informações do Banco</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-slate-500">Total de documentos:</div>
                  <div className="text-slate-800 font-medium">{backupInfo.total_documents?.toLocaleString()}</div>
                  <div className="text-slate-500">Empresas:</div>
                  <div className="text-slate-800">{backupInfo.collections?.empresas || 0}</div>
                  <div className="text-slate-500">Usuários:</div>
                  <div className="text-slate-800">{backupInfo.collections?.users || 0}</div>
                  <div className="text-slate-500">Denúncias:</div>
                  <div className="text-slate-800">{backupInfo.collections?.denuncias || 0}</div>
                  <div className="text-slate-500">Ouvidorias:</div>
                  <div className="text-slate-800">{backupInfo.collections?.ouvidoria || 0}</div>
                  <div className="text-slate-500">Anexos (registros):</div>
                  <div className="text-slate-800">{backupInfo.collections?.anexos || 0}</div>
                </div>
                {backupInfo.files_info && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-slate-500">Arquivos no servidor:</div>
                      <div className="text-slate-800 font-medium">{backupInfo.files_info.files_on_disk}</div>
                      <div className="text-slate-500">Tamanho total:</div>
                      <div className="text-slate-800">{backupInfo.files_info.total_size_mb} MB</div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Download Backup Section */}
            <div className="border rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Download className="w-5 h-5 text-green-600" />
                <span className="font-medium text-slate-800">Baixar Backup</span>
              </div>
              <p className="text-sm text-slate-500 mb-3">
                Faz download de todos os dados do banco em formato JSON. 
                Recomendado antes de fazer qualquer deploy.
              </p>
              <Button
                onClick={handleDownloadBackup}
                disabled={isBackingUp}
                className="w-full bg-green-600 hover:bg-green-700"
                data-testid="download-backup-btn"
              >
                {isBackingUp ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Gerando backup...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Baixar Backup Completo
                  </>
                )}
              </Button>
            </div>
            
            {/* Restore Backup Section */}
            <div className="border rounded-lg p-4 border-amber-200 bg-amber-50">
              <div className="flex items-center gap-2 mb-2">
                <Upload className="w-5 h-5 text-amber-600" />
                <span className="font-medium text-slate-800">Restaurar Backup</span>
              </div>
              <p className="text-sm text-slate-500 mb-3">
                Restaura dados a partir de um arquivo de backup JSON.
              </p>
              
              {/* Restore Mode Selection */}
              <div className="flex gap-2 mb-3">
                <label className={`flex-1 p-2 rounded border text-center cursor-pointer text-sm ${
                  restoreMode === "merge" 
                    ? "border-blue-500 bg-blue-50 text-blue-700" 
                    : "border-slate-200 text-slate-600"
                }`}>
                  <input
                    type="radio"
                    name="restoreMode"
                    value="merge"
                    checked={restoreMode === "merge"}
                    onChange={(e) => setRestoreMode(e.target.value)}
                    className="sr-only"
                  />
                  <span className="font-medium">Merge</span>
                  <span className="block text-xs mt-1">Adiciona/atualiza</span>
                </label>
                <label className={`flex-1 p-2 rounded border text-center cursor-pointer text-sm ${
                  restoreMode === "replace" 
                    ? "border-red-500 bg-red-50 text-red-700" 
                    : "border-slate-200 text-slate-600"
                }`}>
                  <input
                    type="radio"
                    name="restoreMode"
                    value="replace"
                    checked={restoreMode === "replace"}
                    onChange={(e) => setRestoreMode(e.target.value)}
                    className="sr-only"
                  />
                  <span className="font-medium">Replace</span>
                  <span className="block text-xs mt-1">Substitui tudo</span>
                </label>
              </div>
              
              <label className="block">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleRestoreBackup}
                  disabled={isRestoring}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  className={`w-full ${restoreMode === "replace" ? "border-red-300 text-red-700 hover:bg-red-50" : ""}`}
                  disabled={isRestoring}
                  onClick={(e) => e.currentTarget.previousElementSibling.click()}
                  data-testid="restore-backup-btn"
                >
                  {isRestoring ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Restaurando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Selecionar Arquivo de Backup
                    </>
                  )}
                </Button>
              </label>
              
              {restoreMode === "replace" && (
                <p className="text-xs text-red-600 mt-2">
                  ⚠️ Modo REPLACE: Todos os dados atuais serão substituídos!
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
